import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Play, FileText } from 'lucide-react';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import type { Lesson } from '@/models/Lessons';

const LessonsComponent = () => {
	let appContext: { isLogged: boolean; user: string; email: string; role: string };
	appContext = JSON.parse(localStorage.getItem('appContext')!);

	// Stamped over playing video so a leaked recording points back at an account.
	const watermarkLabel = (appContext?.email ?? '').trim() || (appContext?.user ?? '').trim();

	const [lessons, setLessons] = useState<Lesson[]>([]);
	useEffect(() => {
		if (appContext.isLogged) {
			const fetchLessons = async () => {
				const response = await fetch('/api/lessons');

				// The server no longer trusts the localStorage flag. A 401 means the
				// session cookie expired or was dropped, so send them back to login
				// instead of rendering a page that will fail on every click.
				if (response.status === 401) {
					localStorage.setItem('appContext', JSON.stringify({ isLogged: false, user: ' ', email: ' ', role: 'USER' }));
					window.location.replace('/login');
					return;
				}
				if (!response.ok) return;

				const data = await response.json();
				setLessons(data);
			};
			fetchLessons();
		}
	}, []);

	// Show content
	const [isShowLessonContentDialogOpen, setIsShowLessonContentDialogOpen] = useState(false);
	const [lessonShowContentId, setLessonShowContentId] = useState(0);
	const [lessonShowContentTitle, setLessonShowContentTitle] = useState('');
	const [lessonShowContentType, setLessonShowContentType] = useState('فيديو');
	const [lessonShowContentUrl, setLessonShowContentUrl] = useState('');

	const [accessError, setAccessError] = useState('');

	const doShowLessonContent = async (id: number, title: string, type: string) => {
		const url = '/api/lessons/download/' + id + '/type/' + type;

		// Ask the gate before pointing the player at the URL: on refusal the media
		// element would just fail silently, leaving a blank box and no explanation.
		// redirect:'manual' stops here at the 302 rather than pulling the media down.
		try {
			const response = await fetch(url, { redirect: 'manual' });

			if (response.status === 401) {
				localStorage.setItem('appContext', JSON.stringify({ isLogged: false, user: ' ', email: ' ', role: 'USER' }));
				window.location.replace('/login');
				return;
			}
			if (response.status === 403) {
				setAccessError('اشتراكك غير مفعل حاليا. الرجاء التواصل معنا لتفعيل الاشتراك.');
				setLessonShowContentTitle(title);
				setLessonShowContentType(type);
				setLessonShowContentUrl('');
				setIsShowLessonContentDialogOpen(true);
				return;
			}
		} catch (error) {
			console.error('Failed to open lesson content:', error);
		}

		setAccessError('');
		setLessonShowContentId(id);
		setLessonShowContentTitle(title);
		setLessonShowContentType(type);
		setLessonShowContentUrl(url);
		setIsShowLessonContentDialogOpen(true);
	};

	return (
		<div className="flex min-h-screen flex-col" style={{ backgroundColor: '#e6e6b8' }}>
			<div className="top-0 z-10000 w-full px-0 py-3 sm:p-6">
				<Navbar />
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				<div className="text-center font-bold text-3xl sm:text-4xl">ملخصات</div>
				<div className="mt-2 text-center text-muted-foreground text-sm">تصفح جميع الدروس والملخصات المتوفرة</div>
			</div>
			<div className="py-4"></div>

			<div className="flex flex-col gap-4 text-center">
				{!appContext.isLogged && (
					<div>
						{/* The offer, as text rather than the old flyer image: a green plate
						    naming the subject and class, then a yellow plate listing what the
						    summary covers. dir="rtl" keeps the "+" between the two sequences. */}
						<div className="mx-auto w-[90%] max-w-[400px] text-center" dir="rtl">
							<div
								className="rounded-xl px-4 py-5 font-bold text-lg sm:text-xl"
								style={{ backgroundColor: '#1b5e20', color: '#f5f5dc', boxShadow: '0 3px 8px rgba(0,0,0,0.18)', lineHeight: '1.8' }}>
								رياضيات
								<br />
								سنة 3 ثانوي
								<br />
								شعبة آداب وفلسفة
							</div>

							<div
								className="mt-6 rounded-xl px-4 py-6 font-bold text-lg sm:text-xl"
								style={{ backgroundColor: '#ffe600', color: '#1b5e20', boxShadow: '0 3px 8px rgba(0,0,0,0.18)', lineHeight: '1.8' }}>
								ملخص
								<br />
								المتتالية الحسابية + المتتالية الهندسية
								<br />+<br />
								إتجاه تغير دالة
							</div>
						</div>

						<div className="text-center">
							<div
								className="text-2xl sm:text-3xl"
								style={{
									color: '#1b5e20',
									fontWeight: 'bold',
									marginTop: '20px',
									textShadow: '0 0 10px #ffd600',
								}}>
								✨ ب 3000 دج فقط ✨
							</div>
						</div>
						<br />

						<div className="px-4 text-center">
							{/* Three fixed-padding buttons overflowed a phone row; stack them
							    full-width and only go side by side once there is room. */}
							<div className="mt-5 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
								<Link to="/register" className="w-full sm:w-auto">
									<button
										className="w-full cursor-pointer rounded-lg border-0 px-6 py-3 text-lg sm:w-auto sm:px-9 sm:text-xl"
										style={{ background: '#1b5e20', color: 'rgb(236, 213, 7)' }}>
										شراء ملخص
									</button>
								</Link>

								<a href="https://wa.me/213XXXXXXXXX" target="_blank" className="w-full sm:w-auto">
									<button
										className="w-full cursor-pointer rounded-lg border-0 px-6 py-3 text-lg sm:w-auto sm:px-9 sm:text-xl"
										style={{ background: '#25D366', color: 'rgb(236, 213, 7)' }}>
										واتساب
									</button>
								</a>

								<a href="https://www.facebook.com" target="_blank" className="w-full sm:w-auto">
									<button
										className="w-full cursor-pointer rounded-lg border-0 px-6 py-3 text-lg sm:w-auto sm:px-9 sm:text-xl"
										style={{ background: '#1877f2', color: 'rgb(236, 213, 7)' }}>
										Facebook
									</button>
								</a>
							</div>
						</div>
					</div>
				)}

				{lessons.map((lesson) => (
					<div
						key={lesson.id}
						// 60% of a 360px screen left the title in a ~200px column.
						className="mx-auto my-4 w-[90%] rounded-lg p-4 text-center sm:w-3/5 sm:p-5"
						style={{ background: '#ffe600' }}>
						<div
							className="flex cursor-pointer flex-row items-center justify-center gap-2 font-bold"
							style={{ color: '#1b5e20' }}
							onClick={() => doShowLessonContent(lesson.id, lesson.title, lesson.type)}>
							{lesson.type === 'فيديو' ? (
								<>
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
										<Play className="h-5 w-5 text-primary" />
									</div>{' '}
									<div className="min-w-0 break-words text-lg sm:text-[22px]">{lesson.title}</div>
								</>
							) : (
								<>
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
										<FileText className="h-5 w-5 text-primary" />
									</div>{' '}
									<div className="min-w-0 break-words text-lg sm:text-xl">{lesson.title}</div>
								</>
							)}
						</div>
					</div>
				))}
			</div>
			{/* mt-auto eats the leftover column height, so a short lesson list still
			    leaves the footer on the bottom edge of the viewport. The padding keeps
			    the footer well clear of the last button once the page scrolls; it is an
			    inline style because Tailwind has no matching class in the built CSS. */}
			<div className="mt-auto" style={{ paddingTop: '96px' }}>
				<Footer />
			</div>

			{/* Show lesson content dialog */}
			<div dir="rtl">
				<Dialog open={isShowLessonContentDialogOpen} onOpenChange={setIsShowLessonContentDialogOpen}>
					<DialogContent className="flex h-[85vh] w-[calc(100%-1.5rem)] max-w-[900px] flex-col p-3 sm:p-6" dir="rtl">
						<DialogHeader className="shrink-0">
							<DialogTitle className="text-center" dir="rtl">
								{lessonShowContentTitle}
							</DialogTitle>
						</DialogHeader>
						{/* The viewer needs a real height to fill; percentage heights on the
						    old wrapper resolved against an auto-height parent and collapsed. */}
						<DialogDescription className="min-h-0 flex-1" dir="rtl">
							{/* Video gets a real <video> rather than <object>: that is what accepts
							    controlsList/disablePictureInPicture, which drop the download and PiP
							    entries from the native player menu. These are deterrents against
							    casual saving only — they do not stop anyone who opens devtools, and
							    nothing here (or anywhere on the web) can stop a screen recording. */}
							{accessError !== '' ? (
								<div className="text-center font-bold" dir="rtl" style={{ color: 'red', paddingTop: '48px' }}>
									{accessError}
								</div>
							) : lessonShowContentType === 'فيديو' ? (
								<div className="relative h-full w-full">
									<video
										src={lessonShowContentUrl}
										className="h-full w-full"
										controls
										controlsList="nodownload noplaybackrate"
										disablePictureInPicture
										onContextMenu={(e) => e.preventDefault()}>
										المحتوى غير متوفر
									</video>

									{/* The viewer's own email, floated over the picture. It cannot prevent
									    a recording, but it makes a leaked copy traceable to the account it
									    came from, which is the part that actually deters sharing.
									    pointer-events:none keeps it from swallowing clicks on the controls. */}
									<div
										style={{
											position: 'absolute',
											top: '8%',
											left: '6%',
											pointerEvents: 'none',
											color: 'rgba(255,255,255,0.35)',
											fontSize: '14px',
											fontWeight: 'bold',
											textShadow: '0 0 4px rgba(0,0,0,0.6)',
										}}>
										{watermarkLabel}
									</div>
								</div>
							) : (
								<object data={lessonShowContentUrl} className="h-full w-full" dir="rtl">
									المحتوى غير متوفر
								</object>
							)}
						</DialogDescription>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
};

export default LessonsComponent;
